import {BaseLoader} from "./BaseLoader";
import {LoaderEvent} from "../events/LoaderEvent";
import {Utils} from "../utils/Utils"
import * as log from "../utils/log"

declare var DEBUG: boolean;

@log.decoratorConfig({isEnabled: DEBUG, isStylized: true})
export class PIXILoader extends BaseLoader {
    log
    error

    private _pixiLoader: PIXI.loaders.Loader;
    private _textures: any;

    constructor(cacheTimestamp?: string, prependURL?: string, id?: string) {
        super("", cacheTimestamp, prependURL, id);

        this._textures = {};
        this._pixiLoader.baseUrl = this._prependURL ? this._prependURL : "";

        const extensions = (PIXI as any).compressedTextures.detectExtensions((PIXI as any).renderer);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this._pixiLoader as any).before((PIXI as any).compressedTextures.extensionChooser(extensions))
    }

    getData() {
        return null;
    }

    getTexturesCount(): number {
        return Object.keys(this._textures).length
    }

    getTexture(name: string): PIXI.Texture {
        return this._textures[name];
    }

    getTextureSequence(sequenceName: string, extension: string,
                       texturesCount?: number, sequenceDigitsCount: number = 3): Array<PIXI.Texture> {
        const textures: Array<PIXI.Texture> = [];

        if (texturesCount == undefined)
            texturesCount = this.getTexturesCount();

        for (let i = 0; i < texturesCount; i++) {
            textures[i] = this.getTexture(sequenceName +
                Utils.convertToSequenceString(i, sequenceDigitsCount) + "." + extension);
        }

        return textures;
    }

    load() {
        if (this._state == BaseLoader.STATE_LOADING)
            return;
        else if (this._state == BaseLoader.STATE_LOADED) {
            var self = this;
            setTimeout(function () {
                self.onLoaded();
            }, 0);
            return;
        }

        this._lastProgress = 0;
        this._state = BaseLoader.STATE_LOADING;
        this._pixiLoader.load();
    }

    stop() {
        if (this._state != BaseLoader.STATE_LOADING)
            return;

        this._state = BaseLoader.STATE_IDLE;

        this._pixiLoader.reset();
    }

    addResource(name: string, src: string, options?: PIXI.loaders.LoaderOptions) {
        src = src + this._getParameters;

        this._pixiLoader.add(name, src, options);
    }

    dispose() {
        this._pixiLoader.reset();
        this._pixiLoader.off('complete', this.onLoaded, this);
        this._pixiLoader.off('progress', this.onProgress, this);
        this._pixiLoader.off('error', this.onError, this);

        for (let key in this._textures) {
            this._textures[key].destroy(true);
        }

        super.dispose();
    }

    protected getResponseType(): string {
        throw "This method is irrelevant for this type of loader.";
    }

    protected setUpRequest() {
        this._pixiLoader = new PIXI.loaders.Loader();

        this._pixiLoader.on('complete', this.onLoaded, this);
        this._pixiLoader.on('progress', this.onProgress, this);
        this._pixiLoader.on('error', this.onError, this);
    }

    private collectTextures() {
        // TODO: check if this works when the image is loaded as base64 string
        const resources: any = this._pixiLoader.resources;
        for (var resKey in resources) {
            if (resources[resKey].isImage) {
                // check for a json that defines the frames in this image
                const index = resKey.lastIndexOf("_image");
                var jsonName: string = undefined;
                if (index != -1)
                    jsonName = resKey.slice(0, index);

                if (resources[jsonName] && resources[jsonName].isJson) {
                    // we found a json for this image, extract the textures from the json
                    for (var textureName in resources[jsonName].textures)
                        this._textures[textureName] = resources[jsonName].textures[textureName];
                } else {
                    // this image does not have a corresponding json, get the entire
                    // texture for the image
                    this._textures[resKey] = resources[resKey].texture;
                }
            }
        }
    }

    protected onLoaded(event?: any) {
        // there is a bug with pixi which causes the complete handler to be called
        // even if the error handler has been called before that
        if (this._state == BaseLoader.STATE_ERROR) {
            return;
        }

        if (event) {
            var data =
                {
                    progress: event.progress / 100,
                    progressChange: (event.progress - this._lastProgress) / 100
                };

            this._lastProgress = event.progress;

            this.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS, data));

            // TODO: remove this loop in pixi 4
            // This loop is needed because even if a resource fails the complete handler will be called.
            // Also if all resources in the loader fail the complete handler is called before the error handler.
            for (let key in this._pixiLoader.resources) {
                if (this._pixiLoader.resources[key].error != null) {
                    this.onError(null);
                    return;
                }
            }

            this.collectTextures();
        } else
            this.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS,
                {progress: 1, progressChange: 0}));

        this._state = BaseLoader.STATE_LOADED;

        this.dispatchEvent(new LoaderEvent(LoaderEvent.COMPLETE));
    }

    private onProgress(event: any) {
        var data =
            {
                progress: event.progress / 100,
                progressChange: (event.progress - this._lastProgress) / 100
            };

        this._lastProgress = event.progress;

        this.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS, data));
    }

    protected onError(event: any, loader?, resource?) {
        const unknownString = 'Unknown'
        let dumpMessage, errMsg, resourceName, resourceURL
        let unknownReason = false

        try {
            errMsg = event.message
        } catch (e) {
            errMsg = unknownString
        }

        try {
            resourceName = resource.name
            resourceURL = resource.url
        } catch (e) {
            resourceName = unknownString
            resourceURL = unknownString
            unknownReason = true
        }

        if (unknownReason) {
            dumpMessage = `Error: ${errMsg}. Origin of the error is unknown, so dumping the raw data below:`
        } else {
            dumpMessage = `Error: ${errMsg}. Trying to load resource ${resourceName} from ${resourceURL} The resource probably does not exist, check the name and extension.`
        }

        if (DEBUG) {
            this.error(dumpMessage)

            if (unknownReason) {
                this.error('%s %s', loader, resource)
            }
        }

        // TODO: we can remove this check in pixi 4
        // If we have detected that there is a failed resource in the complete handler
        // the state will already be BaseLoader.STATE_ERROR
        if (this._state == BaseLoader.STATE_ERROR)
            return;

        this._state = BaseLoader.STATE_ERROR;

        // When a PIXI.loading.Loader has multiple resources added to it even if one of them fails
        // the loader will continue to load the others and eventually dispatch a complete event.
        // Thats why if we detected a failed resources here we stop the loader immediately.
        // This way if one resource fails the entire loader is marked as failed.
        this._pixiLoader.reset();

        this.dispatchEvent(new LoaderEvent(LoaderEvent.ERROR));
    }
}
